import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import { ReelContainerElement, ReelShape } from '../reelCanvas.types';
import { SHAPE_OPTIONS } from '../reelCanvasStyles';
import { ColorField } from '../controls/ColorField';
import { GlowFields, LabeledSlider, ShadowFields } from '../controls/EffectFields';
import { ContainerElementBody } from '../CanvasElementView';

export interface ContainerElementDialogProps {
    open: boolean;
    element: ReelContainerElement | null;
    onClose: () => void;
    /** Live updates while the dialog is open (the canvas previews immediately). */
    onChange: (patch: Partial<ReelContainerElement>) => void;
}

/** Container SideKick settings: background (incl. transparent / gradient), border, shadow with direction, glow, shape. */
export const ContainerElementDialog: React.FC<ContainerElementDialogProps> = ({ open, element, onClose, onChange }) => {
    const theme = useTheme();
    if (!element) return null;
    const el = element;
    const previewScale = Math.min(1, 360 / el.width, 180 / el.height);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                <CropSquareIcon color="secondary" />
                <Typography variant="h6" component="span" sx={{ fontWeight: 700, flex: 1 }} noWrap>
                    {el.name}
                </Typography>
                <IconButton size="small" aria-label="Close container settings" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                    sx={{
                        borderRadius: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: alpha('#000', 0.6),
                        backgroundImage: 'repeating-conic-gradient(rgba(255,255,255,0.05) 0% 25%, transparent 0% 50%)',
                        backgroundSize: '20px 20px',
                        p: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box sx={{ width: el.width * previewScale, height: el.height * previewScale, position: 'relative' }}>
                        <Box sx={{ width: el.width, height: el.height, transform: `scale(${previewScale})`, transformOrigin: 'top left', position: 'absolute', top: 0, left: 0 }}>
                            <ContainerElementBody element={el} />
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <ColorField label="Background colour" value={el.fillColor} onChange={(c) => onChange({ fillColor: c })} allowGradient allowTransparent />
                    <ColorField label="Border colour" value={el.borderColor} onChange={(c) => onChange({ borderColor: c })} allowTransparent />
                    <LabeledSlider label="Border width" value={el.borderWidth} min={0} max={60} unit="px" onChange={(v) => onChange({ borderWidth: v })} />
                    <LabeledSlider label="Corner radius" value={el.borderRadius} min={0} max={400} unit="px" onChange={(v) => onChange({ borderRadius: v })} />
                </Box>

                <Divider />
                <ShadowFields title="Shadow" value={el.shadow} onChange={(v) => onChange({ shadow: v })} />
                <Divider />
                <GlowFields title="Background glow" value={el.glow} onChange={(v) => onChange({ glow: v })} />
                <Divider />
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Shape
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {SHAPE_OPTIONS.map((s) => (
                            <Button
                                key={s.value}
                                size="small"
                                variant={el.shape === s.value ? 'contained' : 'outlined'}
                                color="secondary"
                                onClick={() => onChange({ shape: s.value as ReelShape })}
                                sx={{ textTransform: 'none' }}
                            >
                                {s.label}
                            </Button>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="secondary" onClick={onClose}>
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContainerElementDialog;
